export type RateSuggestionType = "group" | "taxReceipt" | "memberOnly";

export interface RateSettingIntents {
  group?: { attendees?: number; matchedKeyword: string };
  taxReceipt?: { amountDollars?: number; matchedKeyword: string };
  memberOnly?: { matchedKeyword: string };
}

const MIN_GROUP_ATTENDEES = 2;
const MAX_GROUP_ATTENDEES = 100;

const SEATS_AFTER_NOUN_PATTERN = /\bseats ([2-9]|1[0-6])\b(?! (?:\d|through|to|and|&))/;
const SEATS_BEFORE_NOUN_PATTERN = /\b(\d{1,3}) seats\b(?! (?:available|left|remaining|per table|to a table))/;
const SEATS_PATTERNS = new Set([SEATS_AFTER_NOUN_PATTERN, SEATS_BEFORE_NOUN_PATTERN]);

const SEAT_MAP_TITLE_PATTERN =
  /^row (?!(?:of|to|in|at|on|for|and|the)\b)[a-z]{1,2}\b.+\b(?:seating|mezzanine|orchestra|balcony)\b/;

const FOR_N_PEOPLE_PATTERN =
  /(?<!\b(?:space|room|capacity|spots?) )\bfor (\d{1,3}) (?:people|persons|guests|attendees)\b/;

const NUMBERED_PEOPLE_PATTERNS = [
  /\btable (?:of|for|de|pour) (\d{1,3})\b/,
  /\bfamily of (\d{1,3})\b(?! ?\(?(?:or more|ou plus|\+|and up))/,
  /\bgroupe? (?:of|de) (\d{1,3})\b(?! ?\(?(?:or more|ou plus|\+|and up))/,
  /\badmits? (\d{1,3})\b/,
  /\badmet (\d{1,3})\b/,
  SEATS_AFTER_NOUN_PATTERN,
  /\bteam of (\d{1,3})\b/,
  /\b(\d{1,3}) ?person team\b/,
  /\bseating for (\d{1,3})\b/,
  SEATS_BEFORE_NOUN_PATTERN,
  /(?<!capacite de )(?<!limite de )(?<!\()\b(\d{1,3}) places\b(?!\s+(?:sont|disponibles?|restantes?|left))/,
  FOR_N_PEOPLE_PATTERN,
  /\bpour (\d{1,3}) personnes\b/,
  /\bpara (\d{1,3}) personas\b/,
  /\bmesa (?:de|para) (\d{1,3})\b/,
  /\( ?(\d{1,3}) (?:personn?es?|persons?|people|guests) ?\)/,
];

const NUMBERED_TICKET_PATTERNS = [
  /\b(\d{1,3}) tickets? included\b/,
  /\b(\d{1,3}) billets? inclus\b/,
  /\( ?(\d{1,3}) (?:tickets?|billets?|boletos?|entradas?) ?\)/,
  /\bincludes? (\d{1,3}) (?:tickets?|entries|admissions?)\b/,
  /\b(?:inclut|comprend) (\d{1,3}) billets?\b/,
  /\bincluye (\d{1,3}) (?:boletos?|entradas?)\b/,
  /\b(\d{1,3}) tickets? for \$/,
  /\b(\d{1,3}) billets? pour \d{1,3} ?\$/,
  /\b(?:strip|book|pack|bundle) of (\d{1,3})\b(?! (?:poster|boards?|games?|cards?|credits?|tees?|sodas?|mulligans?))/,
  /\b(?:carnet|livret) de (\d{1,3})\b/,
  /\b\(?(\d{1,3})\)? (?:vip|admission|general admission|ga|adult|event|gala|dinner|luncheon|banquet|concert|show) tickets\b/,
  /\( ?(\d{1,3}) ?\) tickets\b/,
  /\b\(?(\d{1,3})\)? (?!(?:raffle|drink|drinks|meal|beer|wine|bar|food|parking|ride|game|activity|door|lottery|bingo|prize|golf|mulligan|free|complimentary|more|additional|extra) )[a-z]+ tickets\b/,
];

const KNOWN_SIZE_GROUP_TERMS: Record<string, number> = {
  foursome: 4,
  quatuor: 4,
  twosome: 2,
  threesome: 3,
  "pair of tickets": 2,
  "pair of passes": 2,
};

const TITLE_EXACT_GROUP_TERMS: Record<string, number> = { pair: 2, couple: 2, duo: 2 };
const TITLE_EXACT_UNNUMBERED_TERMS = new Set(["family", "famille"]);
const TITLE_COUPLE_PATTERN = /\b(?:couples?(?! of\b)|duo)\b/;

const TITLE_ENDS_WITH_FOR_N_PATTERN = /(?<!\d )\b(?:for|pour) (\d{1,2})$/;

const UNNUMBERED_PEOPLE_PATTERNS = [
  /\bgroup (?:ticket|rate|package)\b/,
  /(?<!\bat (?:a |the |our |your )?)\breserved table\b/,
  /\bfull table\b/,
  /\bbillet de groupe\b/,
  /\btarif de groupe\b/,
  /\btable complete\b/,
  /\btable corporative\b/,
  /(?<!(?:off|from) )(?<!\bat (?:a |the |our |your )?)\bvip table\b/,
  /\btable vip\b/,
  /\bper table\b/,
  /\bpar table\b/,
  /(?<!\bone )(?<!\d )\bper team\b/,
  /\$ ?\d[\d,.]* per team\b/,
  /\bteam (?:registration|entry|ticket|spot|sign ?up)\b/,
  /(?<!\b(?:dessert|registration|check in|sign in|merch|display|vendor|raffle|welcome|info|craft|auction) )\btable sponsors?(?:hip)?\b/,
  /(?<!\bat (?:a |the |our |your )?)\b(?:whole|entire|private) table\b/,
  /\btable (?:purchase|buyout)\b/,
  /(?<!\b(?:your|their) )\b(?:foursome|twosome|threesome)\b/,
  /\bquatuor\b/,
  /\bcouples? (?:ticket|package|entry|admission|pass)\b/,
  /\bfamily (?:pass|package|admission|pack|ticket|rate|entry|registration)\b/,
  /\bper family\b(?! member)/,
  /\bduo (?:ticket|pass|package)\b/,
  /\bpair of (?:tickets|passes)\b/,
  /\b(?:buy|book|purchase|reserve|sponsor) (?:a|an entire|a whole|your) table\b/,
];

const UNNUMBERED_TICKET_PATTERNS = [
  /\b(?:ticket|group|family|team) bundles?\b/,
  /\bbundles? (?:of )?(?:\d{1,3} )?(?:tickets?|admissions?|billets?)\b/,
];

const TABLE_PATTERNS = new Set(
  [...NUMBERED_PEOPLE_PATTERNS, ...UNNUMBERED_PEOPLE_PATTERNS].filter((pattern) => /table/.test(pattern.source))
);

const SINGLE_PURCHASE_PATTERNS = [
  /\$? ?\d[\d,.]*\s?(?:\/|per)\s?(?:person|persona|personne)\b/,
  /\bprice (?:is )?per (?:person|ticket|golfer|player|rider)\b/,
  /\bprix par personne\b/,
  /\d\s*\/\s*persons?\b/,
  /\bindividual (?:tickets?|seats?|registrations?|entry|entries|players?|golfers?|admissions?)\b/,
  /\bsingle (?:tickets?|players?|seats?|entry|entries|admissions?)\b/,
  /\badmission for (?:1|one)\b/,
  /\badmision para (?:1|uno)\b/,
  /\bbillets? individuels?\b/,
  /\bplaces? individuelles?\b/,
  /\badmits? (?:1|one)\b/,
  /\badmet (?:1|un)\b/,
  /\b(?:1|one) seat\b/,
  /\bpaired with\b/,
];

const TITLE_SINGLE_PURCHASE_PATTERNS = [
  /\bper person\b/,
  /\bpar personne\b/,
  /\bper golfer\b/,
  /\bper player\b/,
  /\bindividual\b/,
];

const RAFFLE_CONTEXT_PATTERN =
  /\b(?:raffles?|rifa|50\/50|50 50|door prizes?|cake ?walk|face painting|drink tickets?|game tickets?|ride tickets?|activity tickets?|attraction|carnival|midway|arcade)\b/;
const ADD_ON_TITLE_PATTERN = /\b(?:mulligans?|add ?ons?)\b/;
const BOOTH_TITLE_PATTERN = /\b(?:vendors?|exhibitors?|booth|expo|exposants?|marchands?|kiosque)\b/;
const LODGING_CONTEXT_PATTERN =
  /\b(?:beds?|bedrooms?|bathrooms?|campsites?|camping|cabins?|rv|lodging|accommodations?|occupancy|bunk|dorm|overnight|nightly|per night|(?:\d{1,2}|two|three|four|five|six|seven) nights?)\b/;

interface GroupDetectionContext {
  seatMapTitle: boolean;
  boothTitle: boolean;
  raffle: boolean;
  lodging: boolean;
}

const SPELLED_NUMBERS: Record<string, string> = {
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
  ten: "10",
  eleven: "11",
  twelve: "12",
  deux: "2",
  trois: "3",
  quatre: "4",
  cinq: "5",
  sept: "7",
  huit: "8",
  neuf: "9",
  dix: "10",
  onze: "11",
  douze: "12",
};
const SPELLED_NUMBERS_RE = new RegExp(`\\b(${Object.keys(SPELLED_NUMBERS).join("|")})\\b`, "g");

const replaceSpelledNumbers = (text: string): string =>
  text.replace(SPELLED_NUMBERS_RE, (word) => SPELLED_NUMBERS[word]);

const TAX_RECEIPT_AMOUNT_PATTERNS = [
  /\$ ?(\d{1,6}) (?:tax|charity|charitable|donation|official) receipt\b/,
  /\b(?:tax|charity|charitable|donation|official) receipt of \$? ?(\d{1,6})\b/,
  /\brecu (?:fiscal|officiel|aux fins d'impot|de charite) de (\d{1,6}) ?\$?/,
];

const TAX_RECEIPT_PATTERNS = [
  /\btax receipts?\b/,
  /\btax deductible\b/,
  /\btax deductions?\b/,
  /\btax exemptions?\b/,
  /\bcharity receipts?\b/,
  /\bcharitable receipts?\b/,
  /\bdonation receipts?\b/,
  /\bofficial receipts?\b/,
  /\bdeductible receipts?\b/,
  /\bdeductible donations?\b/,
  /\brecus? fisca(?:l|ux)\b/,
  /\brecus? aux fins d'impots?\b/,
  /\brecus? d'impots?\b/,
  /\bdeductibles? d'impots?\b/,
  /\brecus? officiels?\b/,
  /\brecus? de charite\b/,
];

const MEMBER_ONLY_PATTERNS = [
  /\bmembers? only\b/,
  /\bmembers? exclusive\b/,
  /\bmembership required\b/,
  /\breserved for members\b/,
  /\bfor members\b/,
  /(?<!\b(?:family|board|staff|team|cast|crew|community) )\bmembers? (?:ticket|admission|price|pricing|rate)s?\b/,
  /\bcurrent members?\b/,
  /\bactive members?\b/,
  /\breservee? aux membres\b/,
  /\bmembres seulement\b/,
  /\bmembres uniquement\b/,
  /\bpour les membres\b/,
  /\bexclusifs? aux membres\b/,
  /\badhesions? requises?\b/,
];

const NON_MEMBERS_PATTERN = /\bnon (?:membres?|members?)\b/;

const NEGATION_TOKENS = /\b(?:no|not|non|without|excludes?|excluded|sans|aucune?|pas|jamais)\b/;

const foldText = (raw: string): string =>
  raw
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;|&#160;/g, " ")
    .replace(/[’‘]/g, "'")
    .replace(/[-–—‐‑]/g, " ")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const isNegated = (text: string, matchIndex: number): boolean => {
  const clauseStart =
    Math.max(
      text.lastIndexOf(".", matchIndex),
      text.lastIndexOf("!", matchIndex),
      text.lastIndexOf("?", matchIndex),
      text.lastIndexOf(",", matchIndex),
      text.lastIndexOf(";", matchIndex)
    ) + 1;
  const windowStart = Math.max(clauseStart, matchIndex - 30);
  return NEGATION_TOKENS.test(text.slice(windowStart, matchIndex));
};

const findMatch = (text: string, patterns: RegExp[]): RegExpMatchArray | null => {
  for (const pattern of patterns) {
    const global = new RegExp(pattern.source, `${pattern.flags.replace("g", "")}g`);
    for (const match of text.matchAll(global)) {
      if (!isNegated(text, match.index ?? 0)) {
        return match;
      }
    }
  }
  return null;
};

const selectNumberedGroupPatterns = (context: GroupDetectionContext): RegExp[] => {
  const peoplePatterns = NUMBERED_PEOPLE_PATTERNS.filter(
    (pattern) =>
      !(context.seatMapTitle && SEATS_PATTERNS.has(pattern)) &&
      !(context.boothTitle && TABLE_PATTERNS.has(pattern)) &&
      !(context.lodging && pattern === FOR_N_PEOPLE_PATTERN)
  );
  return context.raffle ? peoplePatterns : [...peoplePatterns, ...NUMBERED_TICKET_PATTERNS];
};

const selectUnnumberedGroupPatterns = (context: GroupDetectionContext): RegExp[] => {
  const peoplePatterns = context.boothTitle
    ? UNNUMBERED_PEOPLE_PATTERNS.filter((pattern) => !TABLE_PATTERNS.has(pattern))
    : UNNUMBERED_PEOPLE_PATTERNS;
  return context.raffle ? peoplePatterns : [...peoplePatterns, ...UNNUMBERED_TICKET_PATTERNS];
};

const detectTitleOnlyGroup = (foldedTitle: string): RateSettingIntents["group"] => {
  const exactTitleSize = TITLE_EXACT_GROUP_TERMS[foldedTitle];
  if (exactTitleSize) {
    return { attendees: exactTitleSize, matchedKeyword: foldedTitle };
  }
  if (TITLE_EXACT_UNNUMBERED_TERMS.has(foldedTitle)) {
    return { matchedKeyword: foldedTitle };
  }
  const couple = foldedTitle.match(TITLE_COUPLE_PATTERN);
  if (couple) {
    return { attendees: 2, matchedKeyword: couple[0] };
  }
  const titleEnd = replaceSpelledNumbers(foldedTitle).match(TITLE_ENDS_WITH_FOR_N_PATTERN);
  if (!titleEnd) {
    return undefined;
  }
  const attendees = Number.parseInt(titleEnd[1], 10);
  return attendees >= MIN_GROUP_ATTENDEES && attendees <= MAX_GROUP_ATTENDEES
    ? { attendees, matchedKeyword: titleEnd[0] }
    : undefined;
};

const detectGroup = (foldedTitle: string, foldedDescription: string): RateSettingIntents["group"] => {
  if (findMatch(foldedTitle, [...SINGLE_PURCHASE_PATTERNS, ...TITLE_SINGLE_PURCHASE_PATTERNS])) {
    return undefined;
  }
  const fullText = `${foldedTitle}. ${foldedDescription}`;
  const context: GroupDetectionContext = {
    seatMapTitle: SEAT_MAP_TITLE_PATTERN.test(foldedTitle),
    boothTitle: BOOTH_TITLE_PATTERN.test(foldedTitle),
    raffle: RAFFLE_CONTEXT_PATTERN.test(fullText) || ADD_ON_TITLE_PATTERN.test(foldedTitle),
    lodging: LODGING_CONTEXT_PATTERN.test(fullText),
  };
  const descriptionVetoed = Boolean(findMatch(foldedDescription, SINGLE_PURCHASE_PATTERNS));
  const sources = descriptionVetoed ? [foldedTitle] : [foldedTitle, foldedDescription];
  const numberedPatterns = selectNumberedGroupPatterns(context);

  const titleIsRaffle = RAFFLE_CONTEXT_PATTERN.test(foldedTitle) || ADD_ON_TITLE_PATTERN.test(foldedTitle);

  let sawBelowMinimum = false;
  for (const [sourceIndex, rawSource] of sources.entries()) {
    const numbered = findMatch(replaceSpelledNumbers(rawSource), numberedPatterns);
    if (!numbered) {
      const titleOnlyGroup = sourceIndex === 0 && !titleIsRaffle ? detectTitleOnlyGroup(foldedTitle) : undefined;
      if (titleOnlyGroup) {
        return titleOnlyGroup;
      }
      continue;
    }
    const attendees = Number.parseInt(numbered[1], 10);
    if (attendees < MIN_GROUP_ATTENDEES) {
      sawBelowMinimum = true;
      continue;
    }
    return attendees > MAX_GROUP_ATTENDEES
      ? { matchedKeyword: numbered[0] }
      : { attendees, matchedKeyword: numbered[0] };
  }

  if (sawBelowMinimum) {
    return undefined;
  }

  const unnumberedSource = context.raffle ? foldedTitle : sources.join(". ");
  const unnumbered = findMatch(unnumberedSource, selectUnnumberedGroupPatterns(context));
  if (!unnumbered) {
    return undefined;
  }
  const knownSize = KNOWN_SIZE_GROUP_TERMS[unnumbered[0]];
  return knownSize ? { attendees: knownSize, matchedKeyword: unnumbered[0] } : { matchedKeyword: unnumbered[0] };
};

const detectTaxReceipt = (foldedText: string): RateSettingIntents["taxReceipt"] => {
  const withAmount = findMatch(foldedText, TAX_RECEIPT_AMOUNT_PATTERNS);
  if (withAmount) {
    return { amountDollars: Number.parseInt(withAmount[1], 10), matchedKeyword: withAmount[0] };
  }
  const mention = findMatch(foldedText, TAX_RECEIPT_PATTERNS);
  return mention ? { matchedKeyword: mention[0] } : undefined;
};

const detectMemberOnly = (foldedText: string): RateSettingIntents["memberOnly"] => {
  if (NON_MEMBERS_PATTERN.test(foldedText)) {
    return undefined;
  }
  const mention = findMatch(foldedText, MEMBER_ONLY_PATTERNS);
  return mention ? { matchedKeyword: mention[0] } : undefined;
};

export const detectRateSettingIntents = (input: { title: string; description: string }): RateSettingIntents => {
  const foldedTitle = foldText(input.title);
  const foldedDescription = foldText(input.description);
  const foldedText = `${foldedTitle}. ${foldedDescription}`;

  const group = detectGroup(foldedTitle, foldedDescription);
  const taxReceipt = detectTaxReceipt(foldedText);
  const memberOnly = detectMemberOnly(foldedText);

  return {
    ...(group ? { group } : {}),
    ...(taxReceipt ? { taxReceipt } : {}),
    ...(memberOnly ? { memberOnly } : {}),
  };
};
